import type { Segment } from 'src/modules/homeownerApp/helpers/segmentation';

import type { Placement, WeightedPoint } from './types';

const isValidPlacement = (
  point: DOMPoint,
  pathElement: SVGPathElement,
  masks?: SVGPathElement[],
) =>
  pathElement.isPointInFill(point) &&
  (!masks || masks.every(mask => !mask.isPointInFill(point)));

/**
 * Find a weighted center point within a search radius of the given
 * point to attempt to place handles away from any path edges
 */
const findWeightedCenterPoint = (
  startX: number,
  startY: number,
  path: SVGPathElement,
  masks?: SVGPathElement[],
  threshold = 50,
) => {
  const svg = path.ownerSVGElement;

  if (!svg) return { x: startX, y: startY, search: [] };

  const point = svg.createSVGPoint();
  const search: WeightedPoint[] = [];

  const stepSize = 5;

  // Radial search within the threshold
  for (let radius = stepSize; radius <= threshold; radius += stepSize) {
    const circumference = 2 * Math.PI * radius;
    const pointsOnCircle = Math.ceil(circumference / stepSize);
    const actualAngleStep = 360 / pointsOnCircle;

    for (let angle = 0; angle < 360; angle += actualAngleStep) {
      const radian = (angle * Math.PI) / 180;
      const x = startX + radius * Math.cos(radian);
      const y = startY + radius * Math.sin(radian);

      point.x = x;
      point.y = y;

      if (isValidPlacement(point, path, masks)) {
        // Weight points closer to initial search point more heavily
        search.push({ x, y, weight: (1 - radius / threshold) ** 2 });
      }
    }
  }

  // Calculate the weighted center point
  const { x, y, totalWeight } = search.reduce(
    (acc, p) => {
      acc.x += p.x * p.weight;
      acc.y += p.y * p.weight;
      acc.totalWeight += p.weight;
      return acc;
    },
    { x: 0, y: 0, totalWeight: 0 },
  );

  return {
    x: x / totalWeight,
    y: y / totalWeight,
    search,
  };
};

export interface PlaceSegmentHandleOptions {
  path: SVGPathElement;
  masks?: SVGPathElement[];
  weighted?: boolean;
}

export const placeSegmentHandle = ({
  path,
  masks,
  weighted = true,
}: PlaceSegmentHandleOptions): Placement | null => {
  const svg = path.ownerSVGElement;

  if (!svg) return null;

  const boundingBox = path.getBBox();

  const centerX = boundingBox.x + boundingBox.width / 2;
  const centerY = boundingBox.y + boundingBox.height / 2;

  const point = svg.createSVGPoint();
  point.x = centerX;
  point.y = centerY;

  if (isValidPlacement(point, path, masks)) {
    return weighted
      ? findWeightedCenterPoint(centerX, centerY, path, masks)
      : { x: centerX, y: centerY, search: [] };
  }

  const stepSize = 5;
  const maxRadius = Math.max(boundingBox.width, boundingBox.height) / 2;

  for (let radius = stepSize; radius < maxRadius; radius += stepSize) {
    for (let angle = 0; angle < 360; angle += stepSize) {
      const radian = (angle * Math.PI) / 180;

      const x = centerX + radius * Math.cos(radian);
      const y = centerY + radius * Math.sin(radian);

      point.x = x;
      point.y = y;

      if (isValidPlacement(point, path, masks)) {
        return weighted
          ? findWeightedCenterPoint(x, y, path, masks)
          : { x, y, search: [] };
      }
    }
  }

  return null;
};

export const instancesToMaterials = (segments: Segment[]) =>
  Array.from(new Set(segments.map(({ material }) => material)));
